'use client';

import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { ENGRAVING_FORM_MODAL_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useUIContext } from '@headless-workspace/core-ui';
import {
    getIcon,
    GlowButton,
    GlowClickable,
    GlowDivider,
    GlowFieldWrapper,
    GlowIcon,
    GlowImage,
    GlowInput,
    GlowPills,
    GlowTextBody,
    GlowTextCaption,
    GlowTitle,
    GlowTypography,
    Icons,
    Pill,
} from '@headless-workspace/glow-ds';
import { Component, NoEmptyArray } from '@headless-workspace/typings';
import { SchemaHelpers } from '@headless-workspace/utils';
import {
    EngraveFormReturnType,
    EngraveFormValue,
    FormUtils,
    getEngraveValidationServerAction,
    ProductFormId,
    useEngraveContext,
} from '../../../../src';

type FieldErrorSchema = {
    type: 'inappropriate';
    terms: string[];
};

type ErrorSchema = {
    line1?: FieldErrorSchema | { type: 'required' };
    line2?: FieldErrorSchema;
    line3?: FieldErrorSchema;
};

const PRODUCT_IMAGE_SIZE = 98;
const ENGRAVE_MAX_LENGTH = 14;
const FIELD_RESET_ERROR_CHARS = 1;
const TERMS_SEPARATOR = ', ';

export type ProductEngraveModalProps = {
    formValueTemplate: EngraveFormValue;
};

export const ProductEngraveFormModal: Component<ProductEngraveModalProps> = ({ formValueTemplate }) => {
    const { product, fields } = formValueTemplate;

    const t = useTranslations('Modal.engrave');
    const { menuModal } = useUIContext();
    const { notifySuccess, notifyError } = useToasts();
    const { engraveFormValues: contextFormValues, setEngraveFormValues: setContextFormValues } = useEngraveContext();

    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [formErrors, setFormErrors] = useState<ErrorSchema>({});

    const getFormValues = useCallback(
        (e: HTMLFormElement) => {
            const formData = new FormData(e);
            return {
                font: formData.get(fields.font.id),
                line1: formData.get(fields.line1.id) || undefined,
                line2: fields.line2 ? formData.get(fields.line2.id) || undefined : undefined,
                line3: fields.line3 ? formData.get(fields.line3.id) || undefined : undefined,
            };
        },
        [fields],
    );

    const pills: Pill[] = useMemo(
        () =>
            fields.font.options.map((option) => {
                if (option.value === 'Manuscript') {
                    return {
                        id: option.value,
                        image: getIcon('Manuscript'),
                    };
                }
                return {
                    id: option.value,
                    label: option.label,
                };
            }),
        [fields],
    );

    const formSchema = useMemo(() => {
        const schemaConstraints: Parameters<typeof FormUtils.generateFormSchema>[1] = {
            [fields.line1.id]: {
                regex: {
                    forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                },
            },
        };

        if (fields.line2) {
            schemaConstraints[fields.line2.id] = {
                regex: {
                    forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                },
            };
        }

        if (fields.line3) {
            schemaConstraints[fields.line3.id] = {
                regex: {
                    forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                },
            };
        }

        return FormUtils.generateFormSchema<EngraveFormReturnType>(Object.values(fields), schemaConstraints);
    }, [fields]);

    const submitForm = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            // ----- Start loading state - reset form errors - prevent default form submission behavior
            setIsValidating(true);
            const formValidationErrors: ErrorSchema = {
                line1: undefined,
                line2: undefined,
                line3: undefined,
            };
            e.preventDefault();

            try {
                // ----- Client side validation : form schema parsing, SQL injections
                const formValues = getFormValues(e.currentTarget);

                const parsedData = formSchema.safeParse(formValues);

                if (!parsedData.success) {
                    const error = parsedData.error;

                    if (SchemaHelpers.hasRequiredError(error, 'line1')) {
                        formValidationErrors.line1 = { type: 'required' };
                        setFormErrors(formValidationErrors);
                    } else {
                        // Unhandled error
                        notifyError(t('error.default'));
                    }
                    return;
                }

                const { data } = parsedData;

                // ----- Server side validation : moderation terms
                const moderationTermsValidationResult = await getEngraveValidationServerAction(data);
                const moderationTermsValidation = unwrapResultSuccess(moderationTermsValidationResult);

                if (!moderationTermsValidation) {
                    notifyError(t('error.default'));
                    return;
                }

                if (moderationTermsValidation.line1.length) {
                    formValidationErrors.line1 = { type: 'inappropriate', terms: moderationTermsValidation.line1 };
                }

                if (moderationTermsValidation.line2.length) {
                    formValidationErrors.line2 = { type: 'inappropriate', terms: moderationTermsValidation.line2 };
                }

                if (moderationTermsValidation.line3.length) {
                    formValidationErrors.line3 = { type: 'inappropriate', terms: moderationTermsValidation.line3 };
                }

                if (formValidationErrors.line1 || formValidationErrors.line2 || formValidationErrors.line3) {
                    setFormErrors(formValidationErrors);
                    return;
                }

                // If the validation succeeds, notify success to the user, register engrave data in context and close the modal.
                notifySuccess(t('label.engraveAdded'));
                setContextFormValues(data);
                menuModal.close(ENGRAVING_FORM_MODAL_ID);
            } finally {
                // Always disable isValidating state after validation
                setIsValidating(false);
            }
        },
        [formSchema, getFormValues, menuModal, notifyError, notifySuccess, setContextFormValues, t],
    );

    // Callback to compute the number of chars for the input's counter
    const callOnChangeCallback = useCallback(
        (onChange: (length: number) => void) => (event: ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            const length = target.value.length;
            onChange(length);
        },
        [],
    );

    const handleClose = useCallback(() => {
        setContextFormValues(undefined);
        menuModal.close(ENGRAVING_FORM_MODAL_ID, true);
    }, [menuModal, setContextFormValues]);

    const handleFormChange = useCallback(
        (e: ChangeEvent<HTMLFormElement>) => {
            const resetFormErrorsForKey = (key: keyof ErrorSchema) => {
                setFormErrors((prevErrors) => ({
                    ...prevErrors,
                    [key]: undefined,
                }));
            };

            const formValues = getFormValues(e.currentTarget);
            for (const [key, value] of Object.entries(formValues)) {
                // If the error is a required field, it is cleared when the user types in the field
                if (formErrors[key as keyof ErrorSchema]?.type === 'required') {
                    if (value) {
                        resetFormErrorsForKey(key as keyof ErrorSchema);
                        return;
                    }
                }
                // When a user empties a field or starts typing after a selection, the field's errors are cleared
                if (!value || (value as string).length === FIELD_RESET_ERROR_CHARS) {
                    resetFormErrorsForKey(key as keyof ErrorSchema);
                }
            }
        },
        [formErrors, getFormValues],
    );

    const getErrorText = (fieldId: keyof ErrorSchema) => {
        switch (formErrors[fieldId]?.type) {
            case 'inappropriate':
                return t('error.inappropriate', {
                    term: formErrors[fieldId].terms.join(TERMS_SEPARATOR),
                });
            case 'required':
                return t('error.required');
            default:
                return undefined;
        }
    };

    const getInputClassname = (fieldId: keyof ErrorSchema) => {
        const commonClassname = clsx(
            'flex-1 p-0.75 rounded-0.5 bg-background-form text-text-primary',
            'rounded-0.5 focus:outline-bold outline',
            'focus:outline-border-selected',
        );

        return clsx(
            commonClassname,
            formErrors[fieldId] ? 'outline-border-error outline-bold' : 'outline-medium outline-border-primary',
        );
    };

    const ModalHeader = () => (
        <section
            className={
                'flex flex-row justify-between items-center p-1 gap-1 border-b-medium border-b-border-subtle border-solid h-headerFixedNavDesktop'
            }
        >
            <GlowTitle text={t('label.title')} fontSize={'title4'} className={'flex-1 text-left'} />
            <GlowClickable
                onClick={handleClose}
                content={{
                    icon: <GlowIcon Icon={Icons.Cross} type={'large'} className={'text-text-primary'} />,
                }}
            />
        </section>
    );

    const ProductInformation = () => (
        <section className={'flex flex-col gap-1'}>
            <div className={'flex flex-row gap-1'}>
                {product.image && (
                    <GlowImage image={product.image} height={PRODUCT_IMAGE_SIZE} width={PRODUCT_IMAGE_SIZE} />
                )}
                <div className={'flex-1 flex flex-col gap-0.5'}>
                    {/* Similar to a GlowTag, but with specific colors */}
                    {/* TODO: Integrate in DS when designed */}
                    <div className={'rounded-0.25 flex items-center gap-0.25 p-0.25 w-fit bg-[#004200] text-[#C0E3BC]'}>
                        <GlowTypography
                            text={t('label.free')}
                            fontSize={'caption'}
                            className={'text-inherit'}
                            fontWeight={'bold'}
                            fontFamily={'brand'}
                        />
                    </div>
                    <GlowTextBody text={t('label.description')} />
                </div>
            </div>
            <div className={'flex flex-col gap-0.25'}>
                <GlowTextBody text={product.brand.name} />
                <GlowTypography fontSize={'title4'} text={product.name} fontWeight={'bold'} />
            </div>
        </section>
    );

    const ModalFooter = () => (
        <section className={'absolute w-full bg-background-l0 bottom-0 -ml-1'}>
            <GlowDivider />
            <div className={'flex items-center justify-center p-1 flex-col gap-0.5'}>
                <GlowButton
                    className={'flex-1 w-full'}
                    label={contextFormValues ? t('action.editEngrave') : t('action.addEngrave')}
                    variant={'primary'}
                    formId={ProductFormId.Engrave}
                    isSpinnerActive={isValidating}
                />
                <GlowButton
                    className={'flex-1 w-full'}
                    label={contextFormValues ? t('action.deleteEngrave') : t('action.cancel')}
                    variant={'secondary'}
                    onClick={handleClose}
                />
            </div>
        </section>
    );

    return (
        <div className={'h-fullViewport w-fullViewport tablet:w-sideMenuTablet flex flex-col bg-background-l3'}>
            <ModalHeader />
            <form
                id={ProductFormId.Engrave}
                onSubmit={submitForm}
                onChange={handleFormChange}
                className={'flex flex-1 flex-col gap-1 p-1'}
                noValidate
            >
                <ProductInformation />
                <GlowDivider />
                <section className={'flex flex-1 flex-col gap-1.5'}>
                    <fieldset>
                        <GlowTextBody
                            TagName={'legend'}
                            fontWeight={'bold'}
                            text={t('label.font')}
                            color={'primary'}
                            className={'pb-1'}
                        />
                        {pills.length > 0 && (
                            <GlowPills
                                key={fields.font.id}
                                type={'radio'}
                                inputName={fields.font.id}
                                contents={pills as NoEmptyArray<Pill>}
                                defaultSelected={contextFormValues?.font ?? fields.font.defaultValue}
                                className={'flex flex-1 flex-row gap-0.5 w-full'}
                                pillClassName={
                                    'flex flex-1 items-center justify-center before:rounded-0.5 has-[:focus-visible]:rounded-0.5'
                                }
                                pillWrapperClassName={
                                    'w-full [&>svg]:h-[21px] gap-0.5 items-center justify-center !rounded-0.5 bg-surface-primary cursor-pointer'
                                }
                            />
                        )}
                    </fieldset>
                    <fieldset className={'flex flex-col gap-1'}>
                        <GlowTextBody
                            TagName={'legend'}
                            fontWeight={'bold'}
                            text={t('label.message')}
                            color={'primary'}
                            className={'pb-1'}
                        />
                        <GlowFieldWrapper
                            label={t('label.firstLine')}
                            fieldId={fields.line1.id}
                            required={fields.line1.isRequired}
                            maxLength={ENGRAVE_MAX_LENGTH}
                            displayMaxLength={true}
                            defaultValue={contextFormValues?.line1 ?? fields.line1.defaultValue}
                            errorText={getErrorText('line1')}
                        >
                            {({ onChange }) => (
                                <GlowInput
                                    className={getInputClassname('line1')}
                                    type={'text'}
                                    name={fields.line1.id}
                                    id={fields.line1.id}
                                    defaultValue={contextFormValues?.line1 ?? fields.line1.defaultValue}
                                    maxLength={ENGRAVE_MAX_LENGTH}
                                    onChange={callOnChangeCallback(onChange)}
                                />
                            )}
                        </GlowFieldWrapper>
                        {fields.line2 && (
                            <GlowFieldWrapper
                                label={t('label.secondLine')}
                                fieldId={fields.line2.id}
                                required={fields.line2.isRequired}
                                maxLength={ENGRAVE_MAX_LENGTH}
                                displayMaxLength={true}
                                defaultValue={contextFormValues?.line2 ?? fields.line2.defaultValue}
                                errorText={getErrorText('line2')}
                            >
                                {({ onChange }) => (
                                    <GlowInput
                                        className={getInputClassname('line2')}
                                        type={'text'}
                                        name={fields.line2?.id}
                                        id={fields.line2?.id}
                                        defaultValue={contextFormValues?.line2 ?? fields.line2?.defaultValue}
                                        maxLength={ENGRAVE_MAX_LENGTH}
                                        onChange={callOnChangeCallback(onChange)}
                                    />
                                )}
                            </GlowFieldWrapper>
                        )}
                        {fields.line3 && (
                            <GlowFieldWrapper
                                label={t('label.thirdLine')}
                                fieldId={fields.line3.id}
                                required={fields.line3.isRequired}
                                maxLength={ENGRAVE_MAX_LENGTH}
                                displayMaxLength={true}
                                defaultValue={contextFormValues?.line3 ?? fields.line3?.defaultValue}
                                errorText={getErrorText('line3')}
                            >
                                {({ onChange }) => (
                                    <GlowInput
                                        className={getInputClassname('line3')}
                                        type={'text'}
                                        name={fields.line3?.id}
                                        id={fields.line3?.id}
                                        defaultValue={contextFormValues?.line3 ?? fields.line3?.defaultValue}
                                        maxLength={ENGRAVE_MAX_LENGTH}
                                        onChange={callOnChangeCallback(onChange)}
                                    />
                                )}
                            </GlowFieldWrapper>
                        )}
                        <GlowTextCaption text={t('label.caption')} className={'text-text-tertiary'} />
                    </fieldset>
                </section>
                <ModalFooter />
            </form>
        </div>
    );
};
